import { useState } from 'react';

/**
 * A thumbnail for a SHORT-TTL signed screenshot URL (feedback / regression images).
 *
 * F5: the signed URL expires (backend TTL ~15-60 min). Once it does, a plain <img>
 * renders the browser's broken-image glyph with no explanation. This wraps the
 * thumbnail with an onError fallback: on a load failure it swaps to a small
 * "Image expired. Refresh to view." affordance instead of a broken icon, so the
 * reporter/coordinator understands a refetch (which re-mints the URL) will fix it.
 *
 * The copy uses plain punctuation (no em/en-dash — an AI-tell / operator-banned on
 * client-facing strings).
 */
interface SignedScreenshotProps {
  /** The signed URL (both the link target and the <img> src). */
  url: string;
  alt: string;
  /** <img> className (size/border/etc). */
  imgClassName?: string;
  /** <a> wrapper className. */
  linkClassName?: string;
  /** data-testid on the <a> (and, suffixed with -expired, on the fallback). */
  testId?: string;
}

export function SignedScreenshot({
  url,
  alt,
  imgClassName = 'max-h-24 rounded border object-cover',
  linkClassName = 'block w-fit',
  testId,
}: SignedScreenshotProps) {
  const [expired, setExpired] = useState(false);

  if (expired) {
    return (
      <span
        data-testid={testId ? `${testId}-expired` : undefined}
        className="inline-flex w-fit items-center rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
      >
        Image expired. Refresh to view.
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={linkClassName}
    >
      <img src={url} alt={alt} className={imgClassName} onError={() => setExpired(true)} />
    </a>
  );
}
